import { useState } from 'react';
import { useI18n } from '@react/i18n';
import { VratkyApi } from '../api/vratkyApi';
import { reasonLabel } from '../reasons';
import { isLookupError, LookupData, Page, TypVraceni, UserInput } from '../types';

interface Props {
    api: VratkyApi;
    userInput: UserInput;
    data: LookupData;
    typVraceni: TypVraceni;
    setStitekInfo: (info: string) => void;
    link: (page: Page) => void;
}

export function Summary({ api, userInput, data, typVraceni, setStitekInfo, link }: Props) {
    const t = useI18n();
    const [error, setError] = useState(false);
    const [busy, setBusy] = useState(false);
    const isCz = data.objednavka.shop_country_code === 'cz';

    const sendData = async () => {
        setBusy(true);
        setError(false);
        try {
            const result = await api.createReturn({
                cislo_dokladu: userInput.user_cislo_dokladu,
                email: userInput.user_email,
                no_order_email: userInput.no_order_email,
                typ: typVraceni,
                cislo_faktury: data.objednavka.cislo_faktury,
                duvod_vraceni: userInput.duvod_vraceni,
                duvod_vraceni_detail: userInput.duvod_vraceni_detail,
                cislo_uctu_user: userInput.cislo_uctu_user,
                iban: userInput.iban_user.toUpperCase(),
                special_conditions: userInput.special_conditions,
                contact_name: userInput.contact_name,
                contact_phone: userInput.contact_phone,
                contact_email: userInput.contact_email,
                items: userInput.vracene_polozky.map((item) => ({
                    product_code: item.product_code,
                    product_name: item.product_name,
                    amount: Number(item.amount),
                })),
            });
            if (isLookupError(result)) {
                setError(true);
                return;
            }
            setStitekInfo(result.stitek_info);
            link('finished');
        } catch {
            setError(true);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="vr-step">
            <h3>{typVraceni === 'vratka' ? t('Souhrn vratky') : t('Souhrn reklamace')}</h3>

            <dl className="vr-summary">
                <dt>{t('Co provádíte:')}</dt>
                <dd>{typVraceni === 'vratka' ? t('Vratka') : t('Reklamace')}</dd>

                <dt>{t('Číslo objednávky:')}</dt>
                <dd>{data.objednavka.cislo_objednavky}</dd>

                <dt>{t('E-mail:')}</dt>
                <dd>{userInput.user_email}</dd>

                <dt>{t('Produkty, které vracíte či reklamujete:')}</dt>
                <dd>
                    {userInput.vracene_polozky.map((item) => (
                        <span key={item.product_code}>
                            {item.product_code} {item.product_name} ({item.amount} ks)<br />
                        </span>
                    ))}
                </dd>

                <dt>{t('Důvod vrácení či reklamace:')}</dt>
                <dd>{reasonLabel(t, userInput.duvod_vraceni)}</dd>

                <dt>{t('Rozepište důvod vrácení či reklamace:')}</dt>
                <dd>{userInput.duvod_vraceni_detail}</dd>

                {userInput.has_special_conditions && (
                    <>
                        <dt>{t('Speciální podmínky domluvené se zástupcem Bola:')}</dt>
                        <dd>{userInput.special_conditions}</dd>
                    </>
                )}

                {isCz ? (
                    <>
                        <dt>{t('Číslo účtu pro případné vrácení peněz:')}</dt>
                        <dd>{userInput.cislo_uctu_user}</dd>
                    </>
                ) : (
                    <>
                        <dt>{t('IBAN číslo účtu pro případné vrácení peněz:')}</dt>
                        <dd>{userInput.iban_user}</dd>
                    </>
                )}

                <dt>{t('Kontaktní jméno:')}</dt>
                <dd>{userInput.contact_name}</dd>

                <dt>{t('Kontaktní telefon:')}</dt>
                <dd>{userInput.contact_phone}</dd>

                <dt>{t('Kontaktní email:')}</dt>
                <dd>{userInput.contact_email}</dd>
            </dl>

            {error && (
                <div className="vr-alert vr-alert--danger" role="alert">
                    {t('Odeslání se nezdařilo. Zkuste to prosím znovu později.')}
                </div>
            )}

            <div className="vr-actions">
                <button type="button" className="vr-btn vr-btn--secondary" onClick={() => link('form')}>{t('Zpět')}</button>
                <button type="button" className="vr-btn vr-btn--primary" disabled={busy} onClick={() => void sendData()}>
                    {t('Odeslat')}
                </button>
            </div>
        </div>
    );
}
